import React, { isValidElement } from 'react';
import { Flex, Text } from '@chakra-ui/react';
import { CustomCollapse } from './../CustomCollapse/CustomCollapse';
import { isUndefined } from 'lodash';

const ROW_NO_BORDER_STYLES = { borderTopWidth: 0 };

interface Props {
  leftLabel: string | boolean | React.ReactNode;
  value: string | boolean | React.ReactNode;
  originalValue?: string | React.ReactNode;
}

// compares two values, which may be primitives, objects, or React elements
export const areValuesEqual = (val1: unknown, val2: unknown) => {
  // Check for React elements first, before generic Object check
  if (isValidElement(val1) || isValidElement(val2)) {
    const component1 = val1 as React.ReactElement;
    const component2 = val2 as React.ReactElement;

    let children1 = component1?.props?.children;
    let children2 = component2?.props?.children;

    if (children1 instanceof Array) children1 = children1.filter((child: unknown) => !isUndefined(child)).join(',');
    if (children2 instanceof Array) children2 = children2.filter((child: unknown) => !isUndefined(child)).join(',');

    // simple comparison by type, props, and key; does not handle nested components, suitable for this use case
    const elemdefn1 = JSON.stringify({
      type: component1?.type,
      props: component1?.props,
      key: component1?.key,
    });
    const elemdefn2 = JSON.stringify({
      type: component2?.type,
      props: component2?.props,
      key: component2?.key,
    });

    return elemdefn1 === elemdefn2 && children1 === children2;
  }

  if (val1 instanceof Object || val2 instanceof Object) {
    // quick comparison before strinifying and comparing; also catches some edge cases where undefined attributes are stripped from JSON text
    if (Object.keys(val1 ?? {}).length !== Object.keys(val2 ?? {}).length) return false;

    return JSON.stringify(val1) === JSON.stringify(val2);
  }

  return val1 === val2;
};

// if right sublabel is not defined we default to right label
const Field: React.FC<Props> = ({ leftLabel, value, originalValue }) => {
  if (value === true) {
    value = 'Yes';
  } else if (value === false) {
    value = 'No';
  }

  if (leftLabel === true) {
    leftLabel = 'Yes';
  } else if (leftLabel === false) {
    leftLabel = 'No';
  }

  if (originalValue === true) {
    originalValue = 'Yes';
  } else if (originalValue === false) {
    originalValue = 'No';
  }

  const hasCurrentValue = value !== undefined && value !== null && value !== '';
  const hasOriginalValue = originalValue !== undefined && originalValue !== null && originalValue !== '';

  const showOriginalValueMissing = !hasOriginalValue && hasCurrentValue;
  const showOriginalValue = !showOriginalValueMissing && !areValuesEqual(value, originalValue);

  // if our current value is undefined, we show "Not included in application" for current value
  const showCurrentValueMissing = !hasCurrentValue;

  const ValueMissing = (props: { lineThrough?: boolean }): React.ReactElement => (
    <Text fontStyle="italic" color="muted" textDecoration={props.lineThrough ? 'line-through' : ''} py={0} my={0}>
      Not included in application
    </Text>
  );

  return (
    <Flex py="2" _first={ROW_NO_BORDER_STYLES} borderTopWidth={1}>
      <CustomCollapse.ColLeft>{leftLabel}</CustomCollapse.ColLeft>
      <Flex flexDir={'column'} width="100%">
        <CustomCollapse.ColRight>{showCurrentValueMissing ? <ValueMissing /> : value}</CustomCollapse.ColRight>
        {showOriginalValueMissing && <ValueMissing lineThrough={true} />}
        {showOriginalValue && <CustomCollapse.ColRight as={'del'}>{originalValue}</CustomCollapse.ColRight>}
      </Flex>
    </Flex>
  );
};

export default Field;
